import type { EnabledMfrrProductionUnit, MfrrBid, MfrrBidResponse, MfrrMarket } from 'api/mfrr/mfrr.types'
import { colors } from 'styles/variables'
import type { TableCellEditRule } from 'ui/components/HourlyDataTable/HourlyDataTable.types'
import Datetime from 'utils/datetime/datetime'
import {
  formatBidInputForDisplay,
  normalizeDecimal,
  parseDecimal,
  parseNonNegativeDecimal,
  serializeDecimalInputText,
} from 'utils/numberUtils/decimalInput'
import type { CmDirection, CmField, EamCopyRow } from './MfrrBidTable.types'

export const CMBID_DIRECTIONS: CmDirection[] = ['up', 'down']

const DECIMAL_INPUT_CHARACTER = /^[\d.,]$/

// Stable reference: a fresh `[]` on every render while query data is undefined would make
// dependent useMemo/useEffect chains re-fire endlessly ("Maximum update depth exceeded").
export const EMPTY_MFRR_BIDS: MfrrBidResponse[] = []

export const MFRR_TABLE_GROUP_BORDER = `1px solid ${colors.grey4}`
export const MFRR_TABLE_CELL_BORDER = `1px solid ${colors.grey3}`
export const MFRR_TABLE_CLOSED_ROW_BORDER = `2px solid ${colors.danger3}`

export function getMfrrDirectionInnerBorder(direction: CmDirection): string {
  return direction === 'up' ? `1px solid ${colors.success2}80` : `1px solid ${colors.danger2}80`
}

export function getMfrrDirectionBackground(direction: CmDirection): string {
  return direction === 'up' ? colors.success1 : colors.danger1
}

export const CM_ROW_COUNT = 24
export const EAM_ROW_COUNT = 96 // 24 hours × 4 quarters
export const EAM_QUARTER_MINUTES = 15
export const MILLISECONDS_PER_MINUTE = 60 * 1000
export const MINUTES_PER_HOUR = 60

export type ParsedCmCellKey = {
  hourIndex: number
  direction: CmDirection
  unitKey: string
  field: CmField
}

export function getCmCellKey(hourIndex: number, direction: CmDirection, unitKey: string, field: CmField): string {
  return `${hourIndex}::${direction}::${unitKey}::${field}`
}

export function getEnabledMfrrProductionUnitKey(unit: EnabledMfrrProductionUnit): string {
  return unit.id.toString()
}

export function parseCmCellKey(cellKey: string): ParsedCmCellKey | null {
  const [hourPart, directionPart, unitKey, fieldPart] = cellKey.split('::')
  const hourIndex = Number(hourPart)

  if (!Number.isInteger(hourIndex)) {
    return null
  }

  if ((directionPart !== 'up' && directionPart !== 'down') || (fieldPart !== 'volume' && fieldPart !== 'price')) {
    return null
  }

  return {
    hourIndex,
    direction: directionPart,
    unitKey,
    field: fieldPart,
  }
}

function isSignedMfrrField(market: MfrrMarket, field: CmField | undefined): boolean {
  return market === 'eam' && field === 'price'
}

function makeMfrrCellEditRule(admitsSign: boolean): TableCellEditRule {
  return {
    kind: 'number',
    admitsSign,
    acceptsCharacter: (character) => DECIMAL_INPUT_CHARACTER.test(character) || (admitsSign && character === '-'),
  }
}

const SIGNED_CELL_EDIT_RULE = makeMfrrCellEditRule(true)
const UNSIGNED_CELL_EDIT_RULE = makeMfrrCellEditRule(false)

export function getMfrrCellEditRule(market: MfrrMarket, field: CmField | undefined): TableCellEditRule {
  return isSignedMfrrField(market, field) ? SIGNED_CELL_EDIT_RULE : UNSIGNED_CELL_EDIT_RULE
}

function parseMfrrPrice(rawValue: string, market: MfrrMarket): number | null {
  return isSignedMfrrField(market, 'price') ? parseDecimal(rawValue) : parseNonNegativeDecimal(rawValue)
}

export function isValidMfrrBidValue(rawValue: string, field: CmField = 'price', market: MfrrMarket = 'cm'): boolean {
  if (rawValue.trim() === '') {
    return true
  }

  if (field === 'volume') {
    return /^\d+$/.test(normalizeDecimal(rawValue))
  }

  return parseMfrrPrice(rawValue, market) !== null
}

export function isValidCmBidValue(rawValue: string, field: CmField = 'price'): boolean {
  return isValidMfrrBidValue(rawValue, field, 'cm')
}

function formatClock(isoTime: ISODateTime): string {
  const date = new Date(isoTime)
  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
}

export function getCmHourLabel(baseStartTime: ISODateTime, hourIndex: number): string {
  const startIso = Datetime.addHours(baseStartTime, hourIndex)

  return `${formatClock(startIso)}-${formatClock(Datetime.addHours(startIso, 1))}`
}

export function getMfrrRowLabel(baseStartTime: ISODateTime, rowIndex: number, market: MfrrMarket): string {
  if (market === 'eam') {
    const startIso = Datetime.addMinutes(baseStartTime, rowIndex * EAM_QUARTER_MINUTES)
    return `${formatClock(startIso)}-${formatClock(Datetime.addMinutes(startIso, EAM_QUARTER_MINUTES))}`
  }
  return getCmHourLabel(baseStartTime, rowIndex)
}

export function getMfrrQuarterIndex(time: ISODateTime, dateStartTime: ISODateTime): number {
  return Math.floor((new Date(time).getTime() - new Date(dateStartTime).getTime()) / (EAM_QUARTER_MINUTES * 60_000))
}

export function getMfrrRowStartTime(baseStartTime: ISODateTime, rowIndex: number, market: MfrrMarket): ISODateTime {
  return market === 'eam'
    ? Datetime.addMinutes(baseStartTime, rowIndex * EAM_QUARTER_MINUTES)
    : Datetime.addHours(baseStartTime, rowIndex)
}

export function getDirectionTotals(
  rowValues: Record<string, string>,
  hourIndex: number,
  direction: CmDirection,
  units: EnabledMfrrProductionUnit[],
  market: MfrrMarket = 'cm'
): { totalVolume: number; totalPrice: number | null } {
  let totalVolume = 0
  let totalPrice = 0
  let hasAnyPrice = false

  for (const unit of units) {
    const unitKey = getEnabledMfrrProductionUnitKey(unit)
    const volumeRaw = rowValues[getCmCellKey(hourIndex, direction, unitKey, 'volume')] ?? ''
    const priceRaw = rowValues[getCmCellKey(hourIndex, direction, unitKey, 'price')] ?? ''

    const parsedVolume = parseNonNegativeDecimal(volumeRaw)
    const parsedPrice = parseMfrrPrice(priceRaw, market)

    if (parsedVolume !== null) {
      totalVolume += parsedVolume
    }

    if (parsedPrice !== null) {
      totalPrice += parsedPrice
      hasAnyPrice = true
    }
  }

  const resolvedTotalPrice = hasAnyPrice ? totalPrice : null

  return { totalVolume, totalPrice: resolvedTotalPrice }
}

export function formatCmReadOnlyValue(value: number | null): string {
  if (value === null) {
    return '-'
  }

  return formatBidInputForDisplay(value.toString())
}

export function mapRowValuesToMfrrBids(
  valuesByRow: Array<Record<string, string>>,
  baseStartTime: ISODateTime,
  units: EnabledMfrrProductionUnit[],
  market: MfrrMarket
): MfrrBid[] {
  const bids: MfrrBid[] = []
  const rowStepMinutes = market === 'eam' ? EAM_QUARTER_MINUTES : 60

  for (let rowIndex = 0; rowIndex < valuesByRow.length; rowIndex++) {
    const rowValues = valuesByRow[rowIndex] ?? {}
    // Bids are keyed by their slot start; start and end share that instant.
    const startTime = Datetime.addMinutes(baseStartTime, rowIndex * rowStepMinutes)

    for (const direction of CMBID_DIRECTIONS) {
      for (const unit of units) {
        const unitKey = getEnabledMfrrProductionUnitKey(unit)
        const volumeRaw = rowValues[getCmCellKey(rowIndex, direction, unitKey, 'volume')] ?? ''
        const priceRaw = rowValues[getCmCellKey(rowIndex, direction, unitKey, 'price')] ?? ''

        const parsedPrice = parseMfrrPrice(priceRaw, market)

        bids.push({
          direction,
          market,
          production_unit_id: unit.id,
          start_time: startTime,
          end_time: startTime,
          volume: parseNonNegativeDecimal(volumeRaw) ?? 0,
          price: parsedPrice ?? 0,
        })
      }
    }
  }

  return bids
}

/**
 * Inverse of `mapRowValuesToMfrrCmBids`: seeds editable table rows from saved bids
 * returned by the backend. Bids outside the visible 24-hour window or referencing
 * unknown production units are ignored.
 */
export function mapMfrrBidsToRowValues(
  bids: Array<Pick<MfrrBidResponse, 'direction' | 'production_unit_id' | 'start_time' | 'volume' | 'price'>>,
  baseStartTime: ISODateTime,
  units: EnabledMfrrProductionUnit[],
  rowCount: number,
  market: MfrrMarket
): Array<Record<string, string>> {
  const rows = Array.from({ length: rowCount }, () => ({}) as Record<string, string>)
  const knownUnitKeys = new Set(units.map((unit) => getEnabledMfrrProductionUnitKey(unit)))
  const baseTime = new Date(baseStartTime).getTime()
  const rowDurationMs = (market === 'eam' ? EAM_QUARTER_MINUTES : 60) * MILLISECONDS_PER_MINUTE

  for (const bid of bids) {
    if (bid.production_unit_id === null) {
      continue
    }

    const hourIndex = Math.round((new Date(bid.start_time).getTime() - baseTime) / rowDurationMs)

    const unitKey = bid.production_unit_id.toString()

    if (hourIndex < 0 || hourIndex >= rowCount || !knownUnitKeys.has(unitKey)) {
      continue
    }

    rows[hourIndex][getCmCellKey(hourIndex, bid.direction, unitKey, 'volume')] = serializeDecimalInputText(bid.volume)
    rows[hourIndex][getCmCellKey(hourIndex, bid.direction, unitKey, 'price')] = serializeDecimalInputText(bid.price)
  }

  return rows
}

export function mapPublishedCmBidVolumesToEamRowValues(
  bids: EamCopyRow[],
  baseStartTime: ISODateTime,
  units: EnabledMfrrProductionUnit[]
): Array<Record<string, string>> {
  const rows = Array.from({ length: EAM_ROW_COUNT }, () => ({}) as Record<string, string>)
  const knownUnitKeys = new Set(units.map((unit) => getEnabledMfrrProductionUnitKey(unit)))
  const baseTime = new Date(baseStartTime).getTime()
  const rowDurationMs = 60 * MILLISECONDS_PER_MINUTE

  for (const bid of bids) {
    if (
      bid.market !== 'cm' ||
      bid.production_unit_id === null ||
      (bid.status !== undefined && bid.status !== 'published')
    ) {
      continue
    }

    const hourIndex = Math.round((new Date(bid.start_time).getTime() - baseTime) / rowDurationMs)

    if (hourIndex < 0 || hourIndex >= CM_ROW_COUNT) {
      continue
    }

    const unitKey = bid.production_unit_id.toString()

    if (!knownUnitKeys.has(unitKey)) {
      continue
    }

    for (let quarterOffset = 0; quarterOffset < 4; quarterOffset++) {
      const rowIndex = hourIndex * 4 + quarterOffset
      rows[rowIndex][getCmCellKey(rowIndex, bid.direction, unitKey, 'volume')] = serializeDecimalInputText(bid.volume)
    }
  }

  return rows
}
